import type { AnalyticsProvider } from './types';
import { pageContext } from './pageContext';

class AnalyticsManager {
  private providers: AnalyticsProvider[] = [];

  addProvider(provider: AnalyticsProvider): void {
    if (this.providers.some((p) => p.name === provider.name)) return;
    this.providers.push(provider);
  }

  removeProvider(name: string): void {
    this.providers = this.providers.filter((p) => p.name !== name);
  }

  getProvider<T extends AnalyticsProvider>(name: string): T | undefined {
    return this.providers.find((p) => p.name === name) as T | undefined;
  }

  capture(event: string, properties?: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    const enriched = { ...pageContext(), ...properties };
    for (const p of this.providers) p.capture(event, enriched);
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    for (const p of this.providers) p.identify(userId, traits);
  }

  reset(): void {
    if (typeof window === 'undefined') return;
    for (const p of this.providers) p.reset();
  }

  pageView(url: string, properties?: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    const enriched = { ...pageContext(), ...properties };
    for (const p of this.providers) p.pageView(url, enriched);
  }

  setPersonProperties(properties: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    for (const p of this.providers) p.setPersonProperties?.(properties);
  }

  register(properties: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    for (const p of this.providers) p.register?.(properties);
  }

  captureException(error: Error, properties?: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    const enriched = { ...pageContext(), ...properties };
    for (const p of this.providers) p.captureException?.(error, enriched);
  }

  optIn(): void {
    for (const p of this.providers) p.optIn?.();
  }

  optOut(): void {
    for (const p of this.providers) p.optOut?.();
  }

  isFeatureEnabled(flag: string): boolean {
    for (const p of this.providers) {
      if (p.isFeatureEnabled) return p.isFeatureEnabled(flag);
    }
    return false;
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    for (const p of this.providers) {
      if (p.getFeatureFlag) return p.getFeatureFlag(flag);
    }
    return undefined;
  }
}

export const analyticsManager = new AnalyticsManager();
